class ErrorLogger {
	#endpoint = '/api/v1/public/log-js-error';

	init() {
		window.addEventListener('error', this.#handleError.bind(this));
		window.addEventListener('unhandledrejection', this.#handleRejection.bind(this));
	}

	#handleError(event) {
		this.#logError({
			type: 'javascript-error',
			url: window.location.href,
			message: event.message,
			filename: event.filename,
			lineno: event.lineno,
			colno: event.colno,
			stack: event.error?.stack,
			source: event.error?.source || null,
			originalError: event.error
		});
	}

	#handleRejection(event) {
		this.#logError({
			type: 'promise-rejection',
			message: event.reason?.message || 'Unhandled Promise Rejection',
			stack: event.reason?.stack,
			// Pro Promise rejection může být reason různý typ
			reason: this.#serializeError(event.reason)
		});
	}

	#logError(errorData) {
		const data = {
			...errorData,
			userAgent: navigator.userAgent,
			url: window.location.href,
			timestamp: new Date().toISOString(),
			// Rozšířené stack trace informace
			enhancedStack: this.#enhanceStackTrace(errorData.stack)

		};

		// Pošli na server
		this.sendToServer(data);

		// Debug do konzole
		console.error('JS Error logged:', data);
	}

	sendToServer(data) {
		// Endpoint musí být platný
		if (!this.#endpoint || typeof this.#endpoint !== 'string') {
			console.error('Endpoint is not valid');
			return;
		}

		// Převod dat na JSON jednou
		const jsonData = JSON.stringify(data);

		// Používáme sendBeacon, pokud ho podporuje prohlížeč
		if (navigator.sendBeacon) {
			const blob = new Blob([jsonData], {
				type: 'application/json'
			});

			const sent = navigator.sendBeacon(this.#endpoint, blob);

			// Pokud odeslání přes sendBeacon nebylo úspěšné, použijeme fetch
			if (sent) {
				return;
			}
		}

		// Fallback na fetch
		try {
			// Odeslání pomocí POST požadavku
			fetch(this.#endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: jsonData
			}).catch((error) => {
				console.error('Fetch error:', error);
			});
		} catch (error) {
			console.error('Unexpected error during fetch:', error);
		}
	}


	// Manuální logování chyb
	static log(message, extra = {}) {
		const logger = new ErrorLogger();
		logger.#logError({
			type: 'manual-log',
			message: message,
			extra: extra,
			stack: new Error().stack // Získáme stack trace pro manuální log
		});
	}


	async #enhanceStackTrace(stack) {
		if (!stack) return null; // Pokud není k dispozici stack trace, vrací null

		const parsedStack = this.#parseStackTrace(stack); // Parosvání chyby na jednotlivé řádky stack trace
		const browserMappedStack = [];

		for (const frame of parsedStack) {
			try {
				// Načtení souboru .map pro každý odpovídající soubor
				const response = await fetch(`${frame.filename}.map`);
				if (!response.ok) {
					console.error(`Source map for ${frame.filename} not found.`);
					browserMappedStack.push(frame); // Ponecháme originální pozici
					continue;
				}

				const rawSourceMap = await response.json();

				// Prohledání zdrojové mapy pro nalezení původního umístění chyby
				const { sources, mappings } = rawSourceMap;
				const [originalLine, originalColumn] = this.#findOriginalPosition(
					frame.line,
					frame.column,
					mappings
				);

				browserMappedStack.push({
					...frame,
					originalFilename: sources[0],
					originalLine,
					originalColumn,
				});
			} catch (error) {
				console.error(`Failed to process source map for ${frame.filename}:`, error);
				browserMappedStack.push(frame); // Pokud dojde k chybě, zanechá původní pozice
			}
		}

		return {
			original: stack,
			parsed: parsedStack,
			browserMapped: browserMappedStack,
		};
	}

	/**
	 * Jednoduchá funkce pro vyhledání linek na základě zdrojové mapy.
	 */
	#findOriginalPosition(line, column, mappings) {
		const lines = mappings.split(';');
		if (line < 1 || line > lines.length) return [line, column];

		const segments = lines[line - 1].split(',');
		for (const segment of segments) {
			const values = this.#decodeVLQ(segment); // Dekódujeme VLQ hodnotu
			if (values[3] >= column) {
				return [values[2] + 1, values[3]]; // Vracíme původní pozici linky
			}
		}
		return [line, column];
	}

	/**
	 * Dekódování VLQ (variable-length quantity) formátu, použitého ve source mapách.
	 */
	#decodeVLQ(str) {
		const result = [];
		let shift = 0;
		let value = 0;

		for (let i = 0; i < str.length; i++) {
			const byte = str.charCodeAt(i) - 65; // VLQ je kódováno offsetem od znaku 'A'
			const isContinuation = (byte & 32) > 0;
			const digit = byte & 31;

			value += digit << shift;
			if (isContinuation) {
				shift += 5;
			} else {
				const isNegative = value & 1;
				value = value >> 1;
				result.push(isNegative ? -value : value);
				value = shift = 0;
			}
		}
		return result;
	}

	#parseStackTrace(stack) {
		if (!stack) return [];

		const lines = stack.split('\n');
		const parsed = [];

		for (const line of lines) {
			// Různé formáty pro různé browsery
			const chromeMatch = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
			const firefoxMatch = line.match(/(.+?)@(.+?):(\d+):(\d+)/);

			if (chromeMatch) {
				parsed.push({
					function: chromeMatch[1],
					filename: chromeMatch[2],
					line: parseInt(chromeMatch[3]),
					column: parseInt(chromeMatch[4])
				});
			} else if (firefoxMatch) {
				parsed.push({
					function: firefoxMatch[1],
					filename: firefoxMatch[2],
					line: parseInt(firefoxMatch[3]),
					column: parseInt(firefoxMatch[4])
				});
			}
		}

		return parsed;
	}

	#serializeError(error) {
		if (error instanceof Error) {
			return {
				name: error.name,
				message: error.message,
				stack: error.stack
			};
		}

		// Pro non-Error objekty
		try {
			return JSON.stringify(error);
		} catch (e) {
			return String(error);
		}
	}


}

export default new ErrorLogger();